import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Wrench, User, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

const BareOrganicProject = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const hydroponicsBenefits = [
    { title: "Accelerated Growth", desc: "Plants grow 30-50% faster than soil cultivation due to optimized nutrient absorption and oxygen availability" },
    { title: "Resource Efficiency", desc: "Requires 90% less water than traditional agriculture through closed-loop recycling systems" },
    { title: "Reduced Interference", desc: "Eliminates soil-borne diseases, pests, and weeds that plague conventional gardening" },
    { title: "Precise Control", desc: "Temperature, humidity, pH levels, and nutrient concentration can be monitored and adjusted for optimal growing conditions" },
    { title: "Environmental Benefits", desc: "Reduced pollution, zero pesticide requirements, and minimal waste generation support sustainable living" },
  ];

  const macroAnalysis = [
    "Growing consumer interest in farm-to-table freshness and food provenance transparency",
    "Increasing environmental consciousness driving sustainable consumption patterns",
    "Wellness movements emphasizing organic, chemical-free food sources",
    "Limited urban green space necessitates vertical and indoor growing solutions",
    "Technology adoption enabling automated home systems and smart appliances",
  ];

  const microAnalysis = [
    "Complex setup processes deter non-expert gardeners from attempting hydroponics",
    "Daily maintenance requirements conflict with busy urban schedules",
    "Plant health monitoring demands constant attention and horticultural knowledge",
    "Soil-based indoor gardening creates mess and attracts pests",
    "Inconsistent growing results lead to plant death and discouragement",
  ];

  const systemComponents = [
    { title: "Grow Light", desc: "Full-spectrum LED lighting facilitating photosynthesis with optimal wavelengths for plant growth stages" },
    { title: "Reservoir Module", desc: "Water storage container housing nutrient solution in Deep Water Culture (DWC) configuration" },
    { title: "Net Pot & Growing Medium", desc: "Suspended plant container with LECA balls (hydroton) and coco peat supporting root structure" },
    { title: "pH Sensor", desc: "Continuous monitoring of nutrient solution acidity/alkalinity, ensuring optimal absorption conditions" },
    { title: "Temperature & Humidity Sensor", desc: "Environmental monitoring tracking ambient conditions affecting plant health" },
    { title: "Air Pump & Air Stone", desc: "Oxygenation system dispersing air bubbles throughout nutrient water, preventing root rot" },
    { title: "RGB Notification Light", desc: "Visual status indicator communicating system health and maintenance needs" },
    { title: "Mobile Application", desc: "IoT companion app delivering trimming notifications, plant schematics, and real-time pH data feeds" },
  ];

  const smartFeatures = [
    "Real-time pH monitoring with historical trend visualization",
    "Growth stage tracking with milestone notifications",
    "Maintenance scheduling based on actual plant conditions",
    "Plant species library with customized care recommendations",
    "Troubleshooting guides for common hydroponic challenges",
    "Community features enabling knowledge sharing among users",
  ];

  const futureEnhancements = [
    { title: "Multi-Plant Systems", desc: "Scaled configurations supporting simultaneous cultivation of multiple plant species" },
    { title: "Advanced Nutrient Management", desc: "Automated nutrient dosing systems eliminate manual solution preparation" },
    { title: "Machine Learning Integration", desc: "Predictive algorithms optimizing growing conditions based on historical performance data" },
    { title: "Modular Expansion", desc: "Stackable units creating vertical farms for maximum space utilization" },
    { title: "Community Platform", desc: "Social features connecting urban farmers for knowledge sharing and seed/cutting exchanges" },
  ];

  const sketchImages = [
    "https://phrygian-tornado-507.notion.site/image/attachment%3Ae90306ff-0eb7-45c7-91f5-4ab2c792c5aa%3Aimage_52.png?table=block&id=2fc22ee1-b108-8091-800e-c49ee0d26613&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=960&userId=&cache=v2",
    "https://phrygian-tornado-507.notion.site/image/attachment%3A11e3f936-8942-4dd7-ad79-54259c2b0855%3Aimage_53.png?table=block&id=2fc22ee1-b108-80ec-8370-c3af45be3d48&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=660&userId=&cache=v2",
    "https://phrygian-tornado-507.notion.site/image/attachment%3A048eba87-907b-43c8-89df-d0e6b50b3e04%3Aimage_54.png?table=block&id=2fc22ee1-b108-806e-9d60-f33ab02b81ab&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=660&userId=&cache=v2",
    "https://phrygian-tornado-507.notion.site/image/attachment%3A62fdcac9-de6f-48b2-a455-36349ad1e84a%3Aimage_55.png?table=block&id=2fc22ee1-b108-80a9-ab17-cc027a98c17b&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=660&userId=&cache=v2",
    "https://phrygian-tornado-507.notion.site/image/attachment%3A0ffa90b2-ecda-4322-a7eb-80ca1b69ee64%3Aimage_48.png?table=block&id=2fc22ee1-b108-803d-950e-eabacbc004ef&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=660&userId=&cache=v2",
    "https://phrygian-tornado-507.notion.site/image/attachment%3Aded6a2b3-ffe4-43d6-bc22-163af2138c03%3Aimage_49.png?table=block&id=2fc22ee1-b108-80cf-9e5a-eb6e47910414&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=660&userId=&cache=v2",
    "https://phrygian-tornado-507.notion.site/image/attachment%3A081f5ab6-a113-4a71-922d-a62041a461f7%3Aimage_50.png?table=block&id=2fc22ee1-b108-80ca-b44e-ffca94185f36&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=660&userId=&cache=v2",
    "https://phrygian-tornado-507.notion.site/image/attachment%3Acf021774-1dd2-4ddd-abc8-9a57409ddf20%3Aimage_51.png?table=block&id=2fc22ee1-b108-80ef-9644-fe0aa63e9982&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=660&userId=&cache=v2",
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-6 left-6 z-50"
      >
        <Button
          onClick={() => navigate("/#projects")}
          variant="outline"
          className="no-shiny h-12 px-4 rounded-full border-white/10 bg-black/50 backdrop-blur-xl text-white hover:bg-white/10"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
      </motion.div>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-red-500/10 text-red-400 border-red-500/20">
              IoT
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] text-white mb-6">
              Bare Organic
            </h1>
            <p className="text-xl md:text-2xl text-white/60 max-w-3xl mb-10">
              Smart Hydroponic System for Space-Constrained Urban Living
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-6 mb-12">
              <div className="flex items-center gap-2 text-white/50">
                <User className="w-5 h-5" />
                <span>Product Designer & IoT Developer</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Calendar className="w-5 h-5" />
                <span>3 Weeks</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Wrench className="w-5 h-5" />
                <span>Fusion360, Keyshot, Miro</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="rounded-3xl overflow-hidden border border-white/5"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/tmn69cpk_untitled%205.png"
              alt="Bare Organic"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Design Vision */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              Design Vision
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white leading-tight max-w-4xl mb-8">
              Bringing Sophisticated Hydroponic Agriculture Into Compact Urban Living Spaces
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed mb-6">
              Bare Organic is a soil-less, automated smart pot that combines transparent housing with integrated technology to transform indoor plant cultivation from a maintenance-intensive hobby into an effortless, data-driven experience.
            </p>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              The name embodies the design philosophy: using transparent technology to process natural entities with minimal intervention—making the growing process both bare (visible) and organic (natural).
            </p>
          </motion.div>
        </div>
      </section>

      {/* What is Hydroponics */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              Technology Foundation
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              What is Hydroponics?
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              Hydroponics is a cultivation method that grows plants without soil by delivering mineral nutrients through water-based solutions. This approach eliminates soil-related complications while accelerating plant growth through optimized nutrient delivery and oxygenation.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hydroponicsBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Analysis */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              Market Analysis
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Understanding the Opportunity
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold font-['Syne'] text-white mb-6">Macro-Level Trends</h3>
              <div className="space-y-4">
                {macroAnalysis.map((item, index) => (
                  <div key={`macroAnalysis-${index}`} className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                    <p className="text-white/60 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-xl font-bold font-['Syne'] text-white mb-6">Micro-Level Challenges</h3>
              <div className="space-y-4">
                {microAnalysis.map((item, index) => (
                  <div key={`microAnalysis-${index}`} className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    <p className="text-white/60 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3A57cc8193-d2a3-437c-b4d6-098c835beac7%3ACollege_Projects.png?table=block&id=2f922ee1-b108-8078-adc6-dd4b18586f7e&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Urban Farming"
                className="w-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3A6b597daa-093a-4540-a2be-6b95dbc678d1%3ACollege_Projects_(1).png?table=block&id=2f922ee1-b108-80ad-b64a-f8a26508bca5&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Hydroponics"
                className="w-full"
              />
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3A8c1878b5-48dd-443f-b73e-7570d939e608%3ACollege_Projects_(4).png?table=block&id=2f922ee1-b108-803a-b5da-cca09c8c2e15&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Urban Farming Micro Analysis"
                className="w-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3Ac32b2681-da00-4d3a-a961-fd6924f92187%3ACollege_Projects_(2).png?table=block&id=2f922ee1-b108-8041-a879-fb87ed6401df&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Hydroponics Micro Analysis"
                className="w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* System Components */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              Product Architecture
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Core System Components
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {systemComponents.map((component, index) => (
              <motion.div
                key={component.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-base font-bold text-white mb-2">{component.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{component.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              User Journey
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              How the System Works
            </h2>
          </motion.div>

          <div className="flex flex-col gap-8 max-w-xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3A9ebb4a06-dca5-4449-a75e-a887b2ae7301%3Aimage_57.png?table=block&id=2fa22ee1-b108-80ce-b2d4-edff36b9a1e9&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Setup"
                className="w-full"
              />
              <div className="p-4 bg-white/5">
                <p className="text-center text-white/60 font-medium">Setup</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3Aea3478f7-0e5e-4bca-98d5-ed493337126d%3Aimage_59.png?table=block&id=2fa22ee1-b108-80ea-9581-c3056225d998&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Maintenance"
                className="w-full"
              />
              <div className="p-4 bg-white/5">
                <p className="text-center text-white/60 font-medium">Maintenance</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3A6bdf9be1-15d0-4326-ab98-f0a1aee1766a%3Aimage_58.png?table=block&id=2fa22ee1-b108-8078-8791-ecc715f05859&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Day to Day"
                className="w-full"
              />
              <div className="p-4 bg-white/5">
                <p className="text-center text-white/60 font-medium">Day to Day Working</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              Design Process
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              Research & Ideation
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              The design process began with comprehensive domain research into hydroponics technology, urban farming trends, and existing smart garden products. Sketching and mind-mapping sessions explored various form factors and feature combinations balancing functionality with visual appeal.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sketchImages.map((img, index) => (
              <motion.div
                key={`sketchImages-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="rounded-2xl overflow-hidden border border-white/5"
              >
                <img src={img} alt={`Sketch ${index + 1}`} className="w-full" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAD Modeling */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              3D Development
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              CAD Modeling
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden border border-white/5 flex flex-col"
            >
              <div className="flex-1 flex items-center justify-center p-4">
                <img
                  src="https://phrygian-tornado-507.notion.site/image/attachment%3A251da72a-0a6a-49b5-ab5c-435b5f89df80%3AGroup_99.png?table=block&id=2fc22ee1-b108-80cf-97d5-ca349036f9e1&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                  alt="CAD Model 1"
                  className="w-full h-auto object-contain"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-3xl overflow-hidden bg-white flex flex-col"
            >
              <div className="flex-1 flex items-center justify-center p-4">
                <img
                  src="https://phrygian-tornado-507.notion.site/image/attachment%3Af9b54738-0011-464a-ae4a-ac42f3d4fea6%3AGroup_100.png?table=block&id=2fc22ee1-b108-80ee-b075-de6c8ab5e6c9&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                  alt="CAD Model 2"
                  className="w-full h-auto object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Smart Features */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              IoT Integration
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              Mobile Application Functionality
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {smartFeatures.map((feature, index) => (
              <motion.div
                key={`smartFeatures-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/5 flex items-start gap-4"
              >
                <div className="w-2 h-2 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                <p className="text-white/60 leading-relaxed">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Renders */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              Final Output
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Renders & Prototype
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden border border-white/5 flex flex-col"
            >
              <div className="flex-1 flex items-center">
                <img
                  src="https://phrygian-tornado-507.notion.site/image/attachment%3Adc4ff03d-c2e6-46e6-b257-4cf014eecd24%3AGroup_101.png?table=block&id=2fc22ee1-b108-80fa-945a-c6ff46e190e7&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                  alt="Renders"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 bg-white/5">
                <p className="text-center text-white/60 font-medium">Renders</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-3xl overflow-hidden border border-white/5 flex flex-col"
            >
              <div className="flex-1 flex items-center">
                <img
                  src="https://phrygian-tornado-507.notion.site/image/attachment%3Adb27020c-8e4b-4c0d-ba1b-a425dff8f036%3AGroup_102.png?table=block&id=2fc22ee1-b108-80dd-b0e6-fba2dea27018&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                  alt="Working Prototype"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 bg-white/5">
                <p className="text-center text-white/60 font-medium">Working Prototype</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Future Enhancements */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-[#0d0d0d] to-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              Looking Ahead
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Future Enhancement Opportunities
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {futureEnhancements.map((enhancement, index) => (
              <motion.div
                key={enhancement.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{enhancement.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{enhancement.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-green-400 tracking-wider uppercase mb-4">
              Conclusion
            </p>
            <h2 className="text-2xl md:text-3xl font-bold font-['Syne'] text-white mb-6 leading-relaxed">
              Bare Organic demonstrates how thoughtful IoT integration can make sustainable practices accessible to urban populations. By removing expertise barriers and time constraints through intelligent automation, the product empowers city dwellers to cultivate fresh food and maintain vibrant indoor gardens regardless of living space limitations or horticultural experience.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Button
              onClick={() => navigate("/#projects")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Portfolio
            </Button>
            
            <Button
              onClick={() => navigate("/#contact")}
              className="h-12 px-8 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-all"
            >
              Get in Touch
            </Button>
            
            <Button
              onClick={() => navigate("/projects/adeefly")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              Next: Adeefly
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BareOrganicProject;
